/**
 * useWebSocket Hook
 *
 * A React hook for managing WebSocket connections with automatic reconnection,
 * connection state tracking, and type-safe message handling.
 *
 * Supports both Claude CLI streaming (/ws/claude) and Terminal PTY (/ws/terminal).
 *
 * Usage:
 *   const { state, send, connect, disconnect } = useWebSocket('/ws/claude', {
 *     onMessage: (data) => console.log('Received:', data),
 *     onConnect: () => console.log('Connected'),
 *     onDisconnect: () => console.log('Disconnected'),
 *   });
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import type {
  ChatInteraction,
  ChatMessage,
  ServiceDefinitionFormSubmissionValue,
} from '@/services/api';
import { bindCurrentWebSocketHandlers } from '@/hooks/webSocketLifecycle';

// ============================================
// Types
// ============================================

/**
 * WebSocket connection states
 */
export type WebSocketState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

/**
 * Configuration options for the WebSocket hook
 */
export interface UseWebSocketOptions<TReceive = unknown, TSend = unknown> {
  /**
   * Called when a message is received from the server
   */
  onMessage?: (data: TReceive) => void;

  /**
   * Called when the connection is established
   */
  onConnect?: () => void;

  /**
   * Called when the connection is closed
   */
  onDisconnect?: (event: CloseEvent) => void;

  /**
   * Called when a connection error occurs
   */
  onError?: (event: Event) => void;

  /**
   * Whether to automatically connect when the hook mounts
   * @default true
   */
  autoConnect?: boolean;

  /**
   * Whether to automatically reconnect after disconnection
   * @default true
   */
  autoReconnect?: boolean;

  /**
   * Maximum number of reconnection attempts
   * @default 5
   */
  maxReconnectAttempts?: number;

  /**
   * Base delay for reconnection in milliseconds (doubles with each attempt)
   * @default 1000
   */
  reconnectBaseDelay?: number;

  /**
   * Maximum delay between reconnection attempts in milliseconds
   * @default 30000
   */
  reconnectMaxDelay?: number;

  /**
   * Custom message serializer (default: JSON.stringify)
   */
  serialize?: (data: TSend) => string;

  /**
   * Custom message deserializer (default: JSON.parse)
   */
  deserialize?: (data: string) => TReceive;

  /**
   * Whether to log debug information
   * @default false
   */
  debug?: boolean;
}

/**
 * Return type for the useWebSocket hook
 */
export interface UseWebSocketReturn<TSend = unknown> {
  /**
   * Current connection state
   */
  state: WebSocketState;

  /**
   * Number of reconnection attempts since last successful connection
   */
  reconnectAttempts: number;

  /**
   * Send a message to the server
   * Returns true if the message was sent, false if not connected
   */
  send: (data: TSend) => boolean;

  /**
   * Connect to the WebSocket server
   */
  connect: () => void;

  /**
   * Disconnect from the WebSocket server
   * @param permanent If true, prevents automatic reconnection
   */
  disconnect: (permanent?: boolean) => void;

  /**
   * Whether the socket is currently connected
   */
  isConnected: boolean;
}

// ============================================
// Provider-neutral Chat WebSocket Types
// ============================================

/**
 * Message types for the provider-aware Chat WebSocket (client → server).
 */
export interface ChatClientMessage {
  type: 'start' | 'cancel' | 'attach' | 'submit_interaction';
  provider?: string;
  message?: string;
  sessionId?: string;
  /** Our chat_sessions.id (server-side row), distinct from sessionId (Claude CLI's --resume id). Used for lineage tagging per ADR-0006. */
  chatSessionId?: string;
  /** Workspace-relative image paths attached to the prompt */
  images?: string[];
  streamId?: string;
  /** Response language preference. Currently fixed to auto by the client. */
  language?: 'auto';
  /** User's local IANA timezone, e.g. America/New_York */
  timezone?: string;
  /** Provider model alias/id */
  model?: string;
  /** Thinking/reasoning level */
  reasoningEffort?: string;
  interactionId?: string;
  interactionRevision?: number;
  optionId?: string;
  definitions?: ServiceDefinitionFormSubmissionValue[];
}

/**
 * Provider-neutral content block in assistant messages.
 */
export interface ChatContentBlock {
  type: 'text' | 'tool_use' | 'tool_result';
  text?: string;
  id?: string;
  name?: string;
  input?: unknown;
  tool_use_id?: string;
  content?: string;
}

/**
 * Event types for the provider-aware Chat WebSocket (server → client).
 */
export interface ChatServerEvent {
  type: 'init' | 'message' | 'result' | 'error' | 'exit' | 'cancelled' | 'activity' | 'interaction' | 'provider_connection_required';
  provider?: string;
  sessionId?: string;
  streamId?: string;
  text?: string;
  content?: ChatContentBlock[];
  result?: string;
  cost?: number;
  code?: number | null;
  signal?: string | null;
  error?: string;
  // Activity event fields (sent every 5 seconds during streaming)
  elapsed?: number;           // ms since stream started
  lastEventType?: string;     // last Claude event type (e.g., 'tool_use', 'text')
  activeToolName?: string;    // currently executing tool name
  activeToolId?: string;      // currently executing tool ID
  interaction?: ChatInteraction;
  responseMessage?: ChatMessage;
  assistantMessage?: ChatMessage;
}

/** Legacy `/ws/claude` aliases. Shared chat code must use the Chat-prefixed types. */
export type ClaudeClientMessage = Omit<ChatClientMessage, 'provider'> & { provider?: 'claude' };
export type ClaudeContentBlock = ChatContentBlock;
export type ClaudeServerEvent = Omit<ChatServerEvent, 'provider'> & { provider?: 'claude' };

// ============================================
// Terminal WebSocket Types
// ============================================

/**
 * Message types for Terminal WebSocket (bidirectional)
 */
export interface TerminalMessage {
  type: 'data' | 'resize';
  data?: string;
  cols?: number;
  rows?: number;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get WebSocket URL from a path
 */
function getWebSocketUrl(path: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}${path}`;
}

/**
 * Calculate reconnect delay with exponential backoff
 */
function getReconnectDelay(
  attempt: number,
  baseDelay: number,
  maxDelay: number
): number {
  const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  // Add some jitter (±20%) to prevent thundering herd
  const jitter = delay * 0.2 * (Math.random() * 2 - 1);
  return Math.round(delay + jitter);
}

// ============================================
// Hook Implementation
// ============================================

/**
 * React hook for managing WebSocket connections
 */
export function useWebSocket<TReceive = unknown, TSend = unknown>(
  path: string,
  options: UseWebSocketOptions<TReceive, TSend> = {}
): UseWebSocketReturn<TSend> {
  const {
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    autoConnect = true,
    autoReconnect = true,
    maxReconnectAttempts = 5,
    reconnectBaseDelay = 1000,
    reconnectMaxDelay = 30000,
    serialize = JSON.stringify,
    deserialize = JSON.parse as (data: string) => TReceive,
    debug = false,
  } = options;

  // State
  const [state, setState] = useState<WebSocketState>('disconnected');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  // Refs for mutable values that shouldn't trigger re-renders
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(autoReconnect);
  const permanentDisconnectRef = useRef(false);
  const connectRef = useRef<() => void>(() => {});

  // Store callbacks in refs to avoid re-triggering effects
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);

  // Update callback refs when they change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
    onErrorRef.current = onError;
  }, [onMessage, onConnect, onDisconnect, onError]);

  // Debug logging helper
  const log = useCallback(
    (message: string, ...args: unknown[]) => {
      if (debug) {
        console.log(`[WebSocket ${path}] ${message}`, ...args);
      }
    },
    [debug, path]
  );

  /**
   * Clear any pending reconnect timeout
   */
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  /**
   * Schedule a reconnection attempt
   */
  const scheduleReconnect = useCallback(() => {
    if (
      !shouldReconnectRef.current ||
      permanentDisconnectRef.current ||
      reconnectAttemptsRef.current >= maxReconnectAttempts
    ) {
      log('Not scheduling reconnect:', {
        shouldReconnect: shouldReconnectRef.current,
        permanent: permanentDisconnectRef.current,
        attempts: reconnectAttemptsRef.current,
        max: maxReconnectAttempts,
      });
      return;
    }

    const delay = getReconnectDelay(
      reconnectAttemptsRef.current,
      reconnectBaseDelay,
      reconnectMaxDelay
    );

    log(`Scheduling reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})`);
    setState('reconnecting');

    reconnectTimeoutRef.current = setTimeout(() => {
      log('Attempting reconnect...');
      reconnectAttemptsRef.current += 1;
      setReconnectAttempts(reconnectAttemptsRef.current);
      // Use ref to avoid circular dependency between scheduleReconnect and connect
      connectRef.current();
    }, delay);
  }, [maxReconnectAttempts, reconnectBaseDelay, reconnectMaxDelay, log]);

  /**
   * Connect to the WebSocket server
   */
  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      log('Already connected or connecting, skipping');
      return;
    }

    // Clear any pending reconnect
    clearReconnectTimeout();

    // Reset permanent disconnect flag
    permanentDisconnectRef.current = false;
    shouldReconnectRef.current = autoReconnect;

    const url = getWebSocketUrl(path);
    log('Connecting to', url);
    setState('connecting');

    const ws = new WebSocket(url);
    wsRef.current = ws;

    bindCurrentWebSocketHandlers({
      socket: ws,
      isCurrent: (candidate) => wsRef.current === candidate,
      deserialize,
      onOpen: () => {
        log('Connected');
        setState('connected');
        reconnectAttemptsRef.current = 0;
        setReconnectAttempts(0);
        onConnectRef.current?.();
      },
      onMessage: (data) => {
        // Handle application-level ping/pong (for proxy compatibility)
        if (data && typeof data === 'object' && 'type' in data) {
          if ((data as { type: string }).type === 'ping') {
            // Respond to ping with pong
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'pong' }));
            }
            return; // Don't pass ping to message handler
          }
        }

        log('Received:', data);
        onMessageRef.current?.(data);
      },
      onError: (event) => {
        log('Error:', event);
        onErrorRef.current?.(event);
      },
      onClose: (event) => {
        log('Closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
        wsRef.current = null;
        setState('disconnected');
        onDisconnectRef.current?.(event);

        // Schedule reconnect if not permanently disconnected
        if (!permanentDisconnectRef.current && shouldReconnectRef.current) {
          scheduleReconnect();
        }
      },
      onParseError: (error) => {
        console.error(`[WebSocket ${path}] Failed to parse message:`, error);
      },
    });
  }, [path, autoReconnect, deserialize, clearReconnectTimeout, log, scheduleReconnect]);

  // Keep connectRef in sync with connect
  connectRef.current = connect;

  /**
   * Disconnect from the WebSocket server
   */
  const disconnect = useCallback(
    (permanent = false) => {
      log('Disconnecting', { permanent });

      if (permanent) {
        permanentDisconnectRef.current = true;
        shouldReconnectRef.current = false;
      }

      clearReconnectTimeout();

      if (wsRef.current) {
        // Close with normal closure code
        wsRef.current.close(1000, 'Client disconnect');
        wsRef.current = null;
      }

      setState('disconnected');
    },
    [clearReconnectTimeout, log]
  );

  /**
   * Send a message to the server
   */
  const send = useCallback(
    (data: TSend): boolean => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        log('Cannot send, not connected');
        return false;
      }

      try {
        const message = serialize(data);
        log('Sending:', data);
        wsRef.current.send(message);
        return true;
      } catch (err) {
        console.error(`[WebSocket ${path}] Failed to send message:`, err);
        return false;
      }
    },
    [serialize, path, log]
  );

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      permanentDisconnectRef.current = true;
      shouldReconnectRef.current = false;
      clearReconnectTimeout();
      if (wsRef.current) {
        const socket = wsRef.current;
        wsRef.current = null;
        socket.close(1000, 'Component unmount');
      }
    };
  }, [autoConnect, connect, clearReconnectTimeout]);

  return {
    state,
    reconnectAttempts,
    send,
    connect,
    disconnect,
    isConnected: state === 'connected',
  };
}

// ============================================
// Typed Hook Variants
// ============================================

/**
 * Typed hook for Claude WebSocket connections
 */
export function useClaudeWebSocket(
  options: Omit<
    UseWebSocketOptions<ClaudeServerEvent, ClaudeClientMessage>,
    'serialize' | 'deserialize'
  > = {}
): UseWebSocketReturn<ClaudeClientMessage> {
  return useWebSocket<ClaudeServerEvent, ClaudeClientMessage>('/ws/claude', options);
}

/**
 * Typed hook for provider-aware chat WebSocket connections
 */
export function useChatWebSocket(
  options: Omit<
    UseWebSocketOptions<ChatServerEvent, ChatClientMessage>,
    'serialize' | 'deserialize'
  > = {}
): UseWebSocketReturn<ChatClientMessage> {
  return useWebSocket<ChatServerEvent, ChatClientMessage>('/ws/chat', options);
}

/**
 * Typed hook for Terminal WebSocket connections
 */
export function useTerminalWebSocket(
  options: Omit<
    UseWebSocketOptions<TerminalMessage, TerminalMessage>,
    'serialize' | 'deserialize'
  > = {}
): UseWebSocketReturn<TerminalMessage> {
  return useWebSocket<TerminalMessage, TerminalMessage>('/ws/terminal', options);
}

export default useWebSocket;
